import { NavLink } from "react-router-dom";
import { LayoutDashboard, Map, PawPrint, Sparkles, MessagesSquare, LogOut, Trophy, Gamepad2, X, Loader2, Dna, Circle, Search, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { RankBadge } from "@/components/RankBadge";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Tableau de bord", short: "RCON", icon: LayoutDashboard, testid: "nav-dashboard", end: true, admin: true },
  { to: "/map", label: "Carte", short: "Carte", icon: Map, testid: "nav-map" },
  { to: "/pals", label: "Pals", short: "Pals", icon: PawPrint, testid: "nav-pals" },
  { to: "/passives", label: "Passifs", short: "Passifs", icon: Sparkles, testid: "nav-passives" },
  { to: "/breeding", label: "Breeding", short: "Breed", icon: Dna, testid: "nav-breeding" },
  { to: "/leaderboard", label: "Classement", short: "Rang", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/discord", label: "Discord", short: "Discord", icon: MessagesSquare, testid: "nav-discord" },
  { to: "/settings", label: "Admin", short: "Admin", icon: ShieldCheck, testid: "nav-settings", admin: true },
];

export const Sidebar = () => {
  const { user, isAdmin, logout, refresh } = useAuth();
  const items = NAV.filter((n) => !n.admin || isAdmin);
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  const [profileOpen, setProfileOpen] = useState(false);
  const [ign, setIgn] = useState("");
  const [savingIgn, setSavingIgn] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openProfile = () => { setIgn(user?.ign || ""); setProfileOpen(true); };
  const saveIgn = async () => {
    setSavingIgn(true);
    try {
      await api.post("/me/ign", { ign });
      await refresh();
      toast.success("Pseudo en jeu enregistré");
      setProfileOpen(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec");
    } finally { setSavingIgn(false); }
  };

  const { data: progress } = useQuery({
    queryKey: ["progress"],
    queryFn: async () => (await api.get("/progress")).data,
    enabled: !!user,
  });
  const defeated = progress?.defeated?.length || 0;

  const { data: online } = useQuery({
    queryKey: ["server-online-count"],
    queryFn: async () => (await api.get("/server/online")).data,
    enabled: !!user,
    refetchInterval: 30000,
  });
  const { data: discord } = useQuery({
    queryKey: ["discord-widget"],
    queryFn: async () => (await api.get("/discord/widget")).data,
    enabled: !!user,
    refetchInterval: 30000,
  });
  const gameCount = online?.online ? online.count : null;
  const discordCount = discord?.enabled ? discord.presence_count : null;

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed z-40 left-0 top-0 h-full w-60 border-r border-border glass flex-col">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <div className="w-9 h-9 rounded-md overflow-hidden border border-primary/40 glow-primary shrink-0">
            <img src="/ksos-logo.jpg" alt="KSOS HUB" className="w-full h-full object-cover" />
          </div>
          <div className="leading-tight">
        <div className="font-head font-bold text-sm tracking-wide text-glow">KSOS HUB</div>
            <div className="text-[10px] text-muted-foreground tracking-[0.2em] uppercase">Server v1.0</div>
          </div>
        </div>
        <div className="px-2 pt-3">
          <button
            data-testid="global-search-trigger"
            onClick={() => setSearchOpen(true)}
            className="w-full flex items-center gap-2.5 rounded-md border border-border bg-black/25 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
          >
            <Search className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">Rechercher…</span>
            <kbd className="text-[10px] font-mono rounded border border-border px-1.5 py-0.5">⌘K</kbd>
          </button>
        </div>
        <nav className="flex-1 py-4 flex flex-col gap-1 px-2 overflow-y-auto scrollbar-thin">
          {items.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-hover))]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
                  <Icon className="w-5 h-5 shrink-0" />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-border p-3">
          {(gameCount != null || discordCount != null) && (
            <div className="mb-2 flex items-center justify-center gap-3 rounded-md bg-black/25 border border-border px-2 py-1.5 text-[11px]" data-testid="live-presence">
              <span className="inline-flex items-center gap-1" title="Joueurs en jeu sur le serveur" style={{ color: "#22C55E" }}>
                <Gamepad2 className="w-3.5 h-3.5" />
                <span className="font-bold" data-testid="live-game-count">{gameCount != null ? gameCount : "–"}</span>
                <span className="text-muted-foreground">en jeu</span>
              </span>
              <span className="w-px h-3 bg-border" />
              <span className="inline-flex items-center gap-1" title="Membres en ligne sur Discord" style={{ color: "#5865F2" }}>
                <Circle className="w-2 h-2 fill-current" />
                <span className="font-bold" data-testid="live-discord-count">{discordCount != null ? discordCount : "–"}</span>
                <span className="text-muted-foreground">Discord</span>
              </span>
            </div>
          )}
          <button onClick={openProfile} data-testid="sidebar-user" className="w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] transition-colors text-left">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center text-primary text-sm font-bold shrink-0">{initial}</div>
            )}
            <div className="leading-tight min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.ign || user?.name || "Dresseur"}</div>
              <div className="mt-0.5"><RankBadge defeated={defeated} size="xs" /></div>
            </div>
            <Gamepad2 className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-1.5 w-full inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile top brand bar */}
      <div className="md:hidden fixed z-40 top-0 inset-x-0 h-14 glass border-b border-border flex items-center gap-2.5 px-4">
        <div className="w-8 h-8 rounded-md overflow-hidden border border-primary/40 glow-primary shrink-0">
          <img src="/ksos-logo.jpg" alt="KSOS HUB" className="w-full h-full object-cover" />
        </div>
        <div className="font-head font-bold text-sm tracking-wide text-glow">KSOS HUB</div>
        <button
          data-testid="global-search-trigger-mobile"
          onClick={() => setSearchOpen(true)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          <Search className="w-4 h-4" />
        </button>
        <button
          data-testid="profile-button-mobile"
          onClick={openProfile}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          <Gamepad2 className="w-4 h-4" />
        </button>
        <button
          data-testid="logout-button-mobile"
          onClick={logout}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-red-400 transition-colors"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed z-40 bottom-0 inset-x-0 h-16 glass border-t border-border flex items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]">
        {items.map(({ to, short, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 flex-1 text-[10px] font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className={`flex items-center justify-center rounded-full px-3 py-1 transition-colors ${isActive ? "bg-primary/15" : ""}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="tracking-wide">{short}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Profile dialog: in-game (Palworld) name */}
      {profileOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setProfileOpen(false)} data-testid="profile-dialog">
          <div className="glass rounded-xl border border-border w-full max-w-sm p-6 fade-up relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setProfileOpen(false)} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-1">
              <Gamepad2 className="w-5 h-5 text-primary" />
              <h3 className="font-head text-xl font-bold">Mon profil</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Renseigne ton <span className="text-primary">pseudo Palworld exact</span> (celui affiché en jeu) pour que l'admin te reconnaisse dans la liste des joueurs connectés.
            </p>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Pseudo en jeu</label>
            <input
              data-testid="ign-input"
              autoFocus
              value={ign}
              onChange={(e) => setIgn(e.target.value)}
              placeholder="Ex: Skredd"
              className="cfg-input mb-4"
              onKeyDown={(e) => { if (e.key === "Enter") saveIgn(); }}
            />
            <button
              data-testid="ign-save"
              onClick={saveIgn}
              disabled={savingIgn}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors disabled:opacity-50"
            >
              {savingIgn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />} Enregistrer
            </button>
          </div>
        </div>
      )}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
